import { logger } from '../utils/logger';

/**
 * Message interface for context management
 */
export interface ContextMessage {
  /** The content of the message */
  content: string;
  /** The role of the sender (user or assistant) */
  role: 'user' | 'assistant';
  /** Optional message ID */
  id?: string;
}

/**
 * System message format for AI context
 */
export interface SystemMessage {
  /** The role is always system for these messages */
  role: 'system';
  /** The content of the system message */
  content: string;
}

/**
 * Props for the useContextManager hook
 */
export interface UseContextManagerProps {
  /** Optional context from the project */
  projectContext?: string;
  /** Optional context from the topic */
  topicContext?: string;
  /** The ID of the current project */
  projectId: string;
  /** The ID of the current topic */
  topicId: string;
  /** Array of chat messages */
  messages: ContextMessage[];
}

/**
 * Combined context structure returned by getCombinedContext
 */
export interface CombinedContext {
  /** Base context from project and topic */
  baseContext: {
    project: string;
    topic: string;
  };
  /** Optional summary of older conversation messages */
  conversationSummary: SystemMessage | null;
  /** Most recent messages (up to 10) */
  recentMessages: ContextMessage[];
}

/**
 * Return type for useContextManager hook
 */
export interface UseContextManagerReturn {
  /** Function to get combined context for AI requests */
  getCombinedContext: () => CombinedContext;
}

/**
 * Hook for managing context in AI conversations
 * Handles combining project context, topic context, and conversation history
 * Creates a summary of older messages when needed
 *
 * @param props - Context manager configuration
 * @returns Object with methods to retrieve combined context
 */
export function useContextManager({
  projectContext,
  topicContext,
  messages,
}: UseContextManagerProps): UseContextManagerReturn {
  /**
   * Creates a summary of older conversation messages when there are many messages
   * @returns A system message containing the summary or null if not needed
   */
  const createConversationSummary = (): SystemMessage | null => {
    logger.component('Context manager - message count', { count: messages.length });

    // Only create summary if we have more than 10 messages
    if (messages.length <= 10) {
      logger.component('Not enough messages for summary', { threshold: 10 });
      return null;
    }

    // Get older messages that won't be included directly
    const olderMessages = messages.slice(0, -10);
    logger.component('Creating summary for older messages', { count: olderMessages.length });

    // Extract the key content from those messages
    const summaryContent = olderMessages
      .map(msg => {
        const prefix = msg.role === 'user' ? 'User: ' : 'Assistant: ';
        return `${prefix}${msg.content}`;
      })
      .join('\n\n');

    logger.component('Summary content created', { length: summaryContent.length });

    return {
      role: 'system',
      content: `IMPORTANT - Earlier conversation history (review this context before responding):\n\n${summaryContent}\n\nYou MUST consider the above conversation history when responding to the user's latest message.`,
    };
  };

  /**
   * Combines all context sources into a structured format for AI requests
   * @returns Combined context object with base context, conversation summary, and recent messages
   */
  const getCombinedContext = (): CombinedContext => {
    logger.component('Getting combined context');
    logger.component('Message array details', {
      totalCount: messages.length,
      projectContextLength: (projectContext || '').length,
      topicContextLength: (topicContext || '').length,
    });

    // Log sample of messages to verify we have the right data
    if (messages.length > 0) {
      const messageSamples = messages.slice(0, Math.min(3, messages.length)).map((msg, idx) => ({
        index: idx,
        role: msg.role,
        preview: msg.content.substring(0, 50) + '...',
      }));

      logger.component('Message samples for context', messageSamples);
    }

    const conversationSummary = createConversationSummary();
    const recentMessages = messages.slice(-10); // Include 10 most recent messages

    logger.component('Context composition', {
      recentMessagesCount: recentMessages.length,
      hasSummary: !!conversationSummary,
    });

    return {
      baseContext: {
        project: projectContext || '',
        topic: topicContext || '',
      },
      conversationSummary,
      recentMessages,
    };
  };

  return {
    getCombinedContext,
  };
}
